import React, { PureComponent } from 'react';
import { Owner } from './owner-list';
import classNames from 'classnames';
import TextField from './text-field';
import isOwnerEmpty from '../lib/is-owner-empty';
import BackButton from './back-button';
import ActionArea from './action-area';
import { validNameKey, handleOnPasteForNameField } from '../lib/valid-key';
import isControllerEmpty from '../lib/is-controller-empty';
import Spinner from './spinner';

const ENTER_SIG_PERSON_HEADING = 'Enter the individual who has significant responsibility to manage your practice';
const SELECT_SIG_PERSON_HEADING = 'Select who has significant responsibility to manage your practice';
const OWNER_CONTROLLER = 'OWNER_AND_CONTROLLER';

class FoldingCard extends PureComponent {
  state = {
    isOpen: false
  }

  handleClick = () => {
    const { onClick, index } = this.props;
    onClick(index);
  }

  handleToggle = () => {
    const isOpen = !this.state.isOpen;
    this.setState({ isOpen });
  }

  render() {
    const {
      title,
      children,
      showExpansion,
      isSelected
    } = this.props;

    const { isOpen } = this.state;
    const dataTestId = `${title.split("(")[0].replaceAll(" ", "-")}radio-button`;
    const radioButtonClasses = classNames('folding-radio-button', {
      'folding-radio-button--selected': isSelected
    });

    const additionalProps = showExpansion
      ? { onClick: this.handleToggle }
      : {};

    return <li className='folding-card'>
      <div className='folding-card-top'>
        <div className='radio-button-container'>
          <div data-test-id={dataTestId} className={radioButtonClasses} onClick={this.handleClick}>
            <div className='dot' />
            <input type='radio' checked={isSelected} name='folding-group' onClick={this.handleClick} />
          </div>
        </div>
        <div {...additionalProps} className='folding-card-title'>{title}</div>
        { showExpansion &&
          <div {...additionalProps} className={classNames('expand-collapse-arrow', {
            'expand-collapse-arrow--collapsed': !isOpen,
            'expand-collapse-arrow--expanded': isOpen
          })}></div>
        }
      </div>
      <div className={classNames('folding-card-body', {
        'folding-card-body--collapsed': !isOpen,
        'folding-card-body--expanded': isOpen
      })}>
        { children }
      </div>
    </li>;
  }
}

FoldingCard.defaultProps = {
  showExpansion: true,
  isSelected: false
};

const OwnerCardList = (props) => {
  const { owners, onClick, selectedOwner } = props;
  return <ul className='folding-card-container'>
    {
      owners.map((owner, index) =>
        <FoldingCard
          index={index}
          onClick={onClick}
          isSelected={index === selectedOwner}
          key={`${owner.firstName}_${owner.lastName}_${index}`}
          title={`${owner.firstName} ${owner.lastName} (Owner #${index + 1})`}>
          <div className='card-field-title'>{`Owner #${index + 1}`}</div>
          <div>{`${owner.firstName} ${owner.lastName}`}</div>
          <div className='card-field-title'>Ownership Share</div>
          <div>{`${owner.share}%`}</div>
          <div className='card-field-title'>Date of Birth</div>
          <div>{owner.dob}</div>
          <div className='card-field-title'>Social Security Number</div>
          <div>{`XXX-XX-${owner.ssn.slice(5)}`}</div>
          <div className='card-field-title'>Residential Address</div>
          <div className='card-field-address'>
            <span>{owner.address.street}{owner.address.aptsuite ? `, ${owner.address.aptsuite}` : ''}</span> <br />
            <span>{owner.address.city}, {owner.address.state}</span><br />
            <span>{owner.address.zip}</span><br />
          </div>
        </FoldingCard>
      )
    }
    <FoldingCard
      isSelected={ selectedOwner === -1}
      onClick={onClick}
      title='Someone else'
      showExpansion={false}
      index={-1}
    />
  </ul>;
};

const getSelectedOwner = ({ owners, significantController }) => {
  const { controller } = significantController;

  const index = owners.findIndex((owner) => owner.type === OWNER_CONTROLLER);
  if (index === -1) {
    return !isControllerEmpty(controller) ? -1 : null;
  } else {
    return index;
  }
};

class SignificantOwner extends PureComponent {
  state = {
    selectedOwner: getSelectedOwner(this.props),
    hasVerified: this.props.significantController.isVerified || false,
    disableAction: false
  }

  handleOwnerListClick = (selectedOwner) => {
    const { setController } = this.props.actions;
    setController(selectedOwner);
    this.setState({ selectedOwner });
  }

  updateFirstName = (e) => {
    const { updateManagementField } = this.props.actions;
    const { value } = e.target;
    updateManagementField('verifierFirstname', value);
  }

  updateLastName = (e) => {
    const { updateManagementField } = this.props.actions;
    const { value } = e.target;
    updateManagementField('verifierLastname', value);
  }

  updateTitle = (e) => {
    const { updateManagementField } = this.props.actions;
    const { value } = e.target;
    updateManagementField('verifierTitle', value);
  }

  handleClick = (e) => {
    e.stopPropagation();
    const hasVerified = !this.state.hasVerified;
    const { updateManagementField } = this.props.actions;

    this.setState({ hasVerified }, () => updateManagementField('isVerified', hasVerified));
  }

  updateController = (index, field, value, errors) => {
    const { updateControllerField } = this.props.actions;
    updateControllerField(`controller.${field}`, value, errors);
  };

  submitData = () => {
    const {
      owners,
      significantController: managementData,
      actions,
      isInReview,
      history
    } = this.props;

    this.setState({ disableAction: true }, () => {
      actions.updateManagementInfo(managementData, owners)
        .then((result) => {
          if (result && result.capAlreadyReview) { return; }
          
          actions.setPreviousRoute('/patientsolutions/enroll/significant-owner');
          const route = isInReview
            ? '/patientsolutions/enroll/review'
            : '/patientsolutions/enroll/ach-information';
          history.push(route);
        })
        .catch(({ response }) => {
          this.setState({ disableAction: false });
          const { status, data } = response;

          if (status && status !== 400) {
            window.location.href = '/patientsolutions/enroll/error';
          }

          if (data && data.redirectTo) {
            window.location.href = data.redirectTo;
          }
        });
      }
    );
  }

  render() {
    const {
      practice,
      owners,
      onboarding,
      smartystreetsconfig,
      significantController,
      errors,
      isInReview
    } = this.props;

    const { processingSubmit, hasCompletedReview } = onboarding;

    const { hasVerified, selectedOwner, disableAction } = this.state;

    let previousRoute = owners.length === 1 && isOwnerEmpty(owners[0])
        ? '/patientsolutions/enroll/ownership'
        : '/patientsolutions/enroll/ownership/add-owners';

    if (isInReview) {
      previousRoute = '/patientsolutions/enroll/review';
    }

    const actionAdditionalProps = {
      className: 'button-submit-next'
    };

    if (disableAction || hasCompletedReview) {
      actionAdditionalProps.disabled = true;
    }

    let showEntryFlow = practice.boSignificantOwnerFlag === 'no';

    const pageHeading = showEntryFlow
      ? ENTER_SIG_PERSON_HEADING
      : SELECT_SIG_PERSON_HEADING;

    return <section className='header-section significant-owner-section'>
      <BackButton {...this.props} previousRoute={previousRoute} />
      <h1 className='section-title'>{pageHeading}</h1>
      <p>We’re required by law to obtain and verify personal information about the individual who has significant management responsibility at your practice.</p>
      {
        !showEntryFlow &&
        <div className='select-owner-list'>
          <OwnerCardList
            onClick={this.handleOwnerListClick}
            selectedOwner={selectedOwner}
            owners={owners}
            showExpansion={false} />
        </div>
      }
      {
        (showEntryFlow || selectedOwner === -1) &&
        <Owner
          {...this.props}
          smartystreetsconfig={smartystreetsconfig}
          isController
          update={this.updateController}
          owner={this.props.significantController.controller}
        />
      }
      {
        (showEntryFlow || selectedOwner !== null) &&
        <div className='verify-section'>
          <h2 className='sub-title-regular'>
            Tell us who entered this information
          </h2>

          <TextField
            data-test-id="verifier-first-name-field"
            defaultValue={significantController.verifierFirstname}
            onBlur={this.updateFirstName}
            onKeyDown={validNameKey}
            onPaste={handleOnPasteForNameField}
            aria-required
            className='input-helper'
            htmlFor='verifier_first_name'
            labelText='First Name'
            labelClassName='section-label'
            errorMsg={errors.verifierFirstname}
          />

          <TextField
            data-test-id="verifier-last-name-field"
            defaultValue={significantController.verifierLastname}
            onBlur={this.updateLastName}
            onKeyDown={validNameKey}
            onPaste={handleOnPasteForNameField}
            aria-required
            className='input-helper'
            htmlFor='verifier_last_name'
            labelText='Last Name'
            labelClassName='section-label'
            errorMsg={errors.verifierLastname}
          />

          <TextField
            data-test-id="verifier-title-field"
            defaultValue={significantController.verifierTitle}
            onBlur={this.updateTitle}
            onKeyDown={validNameKey}
            onPaste={handleOnPasteForNameField}
            aria-required
            className='input-helper'
            htmlFor='verifier_title'
            labelText='Title'
            labelClassName='section-label'
            errorMsg={errors.verifierTitle}
          />

          {
            showEntryFlow &&
            <p className='small-info-text'>
              You verify that none of your practice owners own 25% or more of the business.
            </p>
          }

          <div className='verify-sig-person-section'>
            <div
              onClick={this.handleClick}
              className={classNames('verify-check-container', {
                'verify-check-container--selected': hasVerified,
                'verify-check-container--error field-error-msg': errors.isVerified
              })}>
              <div className='check'></div>
              <input
                data-test-id="verify-checkbox"
                id='verify-checkbox'
                className='verify-check'
                type='checkbox'
                checked={hasVerified}
                onClick={this.handleClick}
                />
            </div>
            <div className='verify-text-container'><label className='verify-label-text small-info-text' htmlFor='verify-checkbox'>In completing this page, I certify that, to the best of my knowledge, the information is complete and correct.</label></div>
          </div>

          <p className='legal-disclosure'>
            To help the government fight the funding of terrorism and money laundering activities, federal regulations require all financial institutions to obtain, verify and record information that identifies each person who opens an account. What this means for you: When you open an account, we will ask your name, address, date of birth and other information that will allow us to identify you.
          </p>
        </div>
      }

      <ActionArea
        {...actionAdditionalProps}
        {...this.props}
        onSubmit={this.submitData} />

      { processingSubmit && <Spinner processingSubmit={processingSubmit}/> }

    </section>;
  }
}

export default SignificantOwner;

